/**
 * Fantasy Console 0 - Code Editor Autocomplete
 *
 * Provides autocomplete suggestions for the Fantasy Console API functions.
 */

import {
  CompletionContext,
  CompletionResult,
  Completion,
} from '@codemirror/autocomplete'

interface APIFunction {
  label: string
  type: 'function' | 'keyword' | 'variable'
  info: string
  detail?: string
  apply?: string
  boost?: number
}

// Fantasy Console API function definitions with JSDoc-style documentation
const API_FUNCTIONS: APIFunction[] = [
  // ==================== DRAWING FUNCTIONS ====================
  {
    label: 'cls',
    type: 'function',
    info: 'Clear the screen with a color',
    detail: '(col?: number) => void',
    apply: 'cls()',
    boost: 10,
  },
  {
    label: 'pset',
    type: 'function',
    info: 'Set a pixel at (x, y) to a color',
    detail: '(x: number, y: number, col?: number) => void',
    apply: 'pset(/*x*/, /*y*/)',
    boost: 8,
  },
  {
    label: 'pget',
    type: 'function',
    info: 'Get the color of a pixel at (x, y)',
    detail: '(x: number, y: number) => number',
    apply: 'pget(/*x*/, /*y*/)',
    boost: 5,
  },
  {
    label: 'rect',
    type: 'function',
    info: 'Draw a rectangle outline at (x, y) with width w and height h',
    detail:
      '(x: number, y: number, w: number, h: number, col?: number) => void',
    apply: 'rect(/*x*/, /*y*/, /*w*/, /*h*/)',
    boost: 8,
  },
  {
    label: 'rectfill',
    type: 'function',
    info: 'Draw a filled rectangle at (x, y) with width w and height h',
    detail:
      '(x: number, y: number, w: number, h: number, col?: number) => void',
    apply: 'rectfill(/*x*/, /*y*/, /*w*/, /*h*/)',
    boost: 9,
  },
  {
    label: 'circ',
    type: 'function',
    info: 'Draw a circle outline at (x, y) with radius r',
    detail: '(x: number, y: number, r: number, col?: number) => void',
    apply: 'circ(/*x*/, /*y*/, /*r*/)',
    boost: 8,
  },
  {
    label: 'circfill',
    type: 'function',
    info: 'Draw a filled circle at (x, y) with radius r',
    detail: '(x: number, y: number, r: number, col?: number) => void',
    apply: 'circfill(/*x*/, /*y*/, /*r*/)',
    boost: 9,
  },
  {
    label: 'oval',
    type: 'function',
    info: 'Draw an oval outline at (x, y) with width w and height h',
    detail:
      '(x: number, y: number, w: number, h: number, col?: number) => void',
    apply: 'oval(/*x*/, /*y*/, /*w*/, /*h*/)',
    boost: 6,
  },
  {
    label: 'ovalfill',
    type: 'function',
    info: 'Draw a filled oval at (x, y) with width w and height h',
    detail:
      '(x: number, y: number, w: number, h: number, col?: number) => void',
    apply: 'ovalfill(/*x*/, /*y*/, /*w*/, /*h*/)',
    boost: 6,
  },
  {
    label: 'line',
    type: 'function',
    info: 'Draw a line from (x0, y0) to (x1, y1)',
    detail:
      '(x0: number, y0: number, x1: number, y1: number, col?: number) => void',
    apply: 'line(/*x0*/, /*y0*/, /*x1*/, /*y1*/)',
    boost: 8,
  },
  {
    label: 'print',
    type: 'function',
    info: 'Print text at (x, y) with optional color',
    detail: '(str: string, x?: number, y?: number, col?: number) => void',
    apply: 'print(/*text*/, /*x*/, /*y*/)',
    boost: 10,
  },
  {
    label: 'printXL',
    type: 'function',
    info: 'Print XL text at (x, y) with optional color',
    detail: '(str: string, x?: number, y?: number, col?: number) => void',
    apply: 'printXL(/*text*/, /*x*/, /*y*/)',
    boost: 10,
  },
  {
    label: 'color',
    type: 'function',
    info: 'Set the default drawing color (0-15)',
    detail: '(col: number) => void',
    apply: 'color(/*col*/)',
    boost: 7,
  },

  // ==================== SPRITE FUNCTIONS ====================
  {
    label: 'spr',
    type: 'function',
    info: 'Draw sprite n at (x, y). Optional: width/height in sprites, flip flags',
    detail:
      '(n: number, x: number, y: number, w?: number, h?: number, flip_x?: boolean, flip_y?: boolean) => void',
    apply: 'spr(/*n*/, /*x*/, /*y*/)',
    boost: 10,
  },
  {
    label: 'sspr',
    type: 'function',
    info: 'Draw a region from the sprite sheet. Stretch from (sx,sy,sw,sh) to (dx,dy,dw,dh)',
    detail:
      '(sx: number, sy: number, sw: number, sh: number, dx: number, dy: number, dw?: number, dh?: number, flip_x?: boolean, flip_y?: boolean) => void',
    apply: 'sspr(/*sx*/, /*sy*/, /*sw*/, /*sh*/, /*dx*/, /*dy*/)',
    boost: 7,
  },
  {
    label: 'pal',
    type: 'function',
    info: 'Remap color c0 to c1. Call with no args to reset palette',
    detail: '(c0?: number, c1?: number, p?: number) => void',
    apply: 'pal(/*c0*/, /*c1*/)',
    boost: 6,
  },
  {
    label: 'palt',
    type: 'function',
    info: 'Set transparency for color col. t=true makes the color transparent',
    detail: '(col?: number, t?: boolean) => void',
    apply: 'palt(/*col*/, /*transparent*/)',
    boost: 6,
  },

  // ==================== MAP FUNCTIONS ====================
  {
    label: 'map',
    type: 'function',
    info: 'Draw a section of the map. cel_x/y is map position, sx/sy is screen position, cel_w/h is size in tiles',
    detail:
      '(cel_x: number, cel_y: number, sx: number, sy: number, cel_w: number, cel_h: number, layer?: number) => void',
    apply: 'map(/*cel_x*/, /*cel_y*/, /*sx*/, /*sy*/, /*cel_w*/, /*cel_h*/)',
    boost: 8,
  },
  {
    label: 'mget',
    type: 'function',
    info: 'Get the sprite number at map position (x, y)',
    detail: '(x: number, y: number) => number',
    apply: 'mget(/*x*/, /*y*/)',
    boost: 7,
  },
  {
    label: 'mset',
    type: 'function',
    info: 'Set the sprite number at map position (x, y)',
    detail: '(x: number, y: number, val: number) => void',
    apply: 'mset(/*x*/, /*y*/, /*val*/)',
    boost: 7,
  },

  // ==================== CAMERA & SCREEN ====================
  {
    label: 'camera',
    type: 'function',
    info: 'Set camera offset. All drawing operations are offset by (-x, -y)',
    detail: '(x?: number, y?: number) => void',
    apply: 'camera(/*x*/, /*y*/)',
    boost: 7,
  },
  {
    label: 'clip',
    type: 'function',
    info: 'Set clipping rectangle. Drawing outside this area is clipped. Call with no args to reset',
    detail: '(x?: number, y?: number, w?: number, h?: number) => void',
    apply: 'clip(/*x*/, /*y*/, /*w*/, /*h*/)',
    boost: 5,
  },

  // ==================== INPUT FUNCTIONS ====================
  {
    label: 'btn',
    type: 'function',
    info: 'Check if button is currently pressed. 0=left, 1=right, 2=up, 3=down, 4=B(z), 5=A(x), 6=Start(Space), 7=Select(Shift)',
    detail: '(button_id: number) => boolean',
    apply: 'btn(/*button_id*/)',
    boost: 10,
  },
  {
    label: 'btnp',
    type: 'function',
    info: 'Check if button was just pressed this frame. 0=left, 1=right, 2=up, 3=down, 4=B(z), 5=A(x), 6=Start(Space), 7=Select(Shift)',
    detail: '(button_id: number) => boolean',
    apply: 'btnp(/*button_id*/)',
    boost: 9,
  },

  // ==================== AUDIO FUNCTIONS ====================
  {
    label: 'sfx',
    type: 'function',
    info: 'Play SFX n (0-63). Use channel 0-3 or omit for auto. offset = start note 0-31. n=-1 stop channel, n=-2 stop looping',
    detail: '(n: number, channel?: number, offset?: number) => void',
    apply: 'sfx(/*n*/, /*channel*/, /*offset*/)',
    boost: 8,
  },

  // ==================== MATH FUNCTIONS ====================
  {
    label: 'abs',
    type: 'function',
    info: 'Return the absolute value of x',
    detail: '(x: number) => number',
    apply: 'abs(/*x*/)',
    boost: 6,
  },
  {
    label: 'sgn',
    type: 'function',
    info: 'Return the sign of x: -1, 0, or 1',
    detail: '(x: number) => number',
    apply: 'sgn(/*x*/)',
    boost: 5,
  },
  {
    label: 'min',
    type: 'function',
    info: 'Return the smaller of x and y',
    detail: '(x: number, y: number) => number',
    apply: 'min(/*x*/, /*y*/)',
    boost: 7,
  },
  {
    label: 'max',
    type: 'function',
    info: 'Return the larger of x and y',
    detail: '(x: number, y: number) => number',
    apply: 'max(/*x*/, /*y*/)',
    boost: 7,
  },
  {
    label: 'mid',
    type: 'function',
    info: 'Return the middle value of x, y, and z',
    detail: '(x: number, y: number, z: number) => number',
    apply: 'mid(/*x*/, /*y*/, /*z*/)',
    boost: 6,
  },
  {
    label: 'flr',
    type: 'function',
    info: 'Round x down to the nearest integer',
    detail: '(x: number) => number',
    apply: 'flr(/*x*/)',
    boost: 7,
  },
  {
    label: 'ceil',
    type: 'function',
    info: 'Round x up to the nearest integer',
    detail: '(x: number) => number',
    apply: 'ceil(/*x*/)',
    boost: 6,
  },
  {
    label: 'sqrt',
    type: 'function',
    info: 'Return the square root of x',
    detail: '(x: number) => number',
    apply: 'sqrt(/*x*/)',
    boost: 6,
  },
  {
    label: 'sin',
    type: 'function',
    info: 'Return the sine of x (x is in 0..1 range, not radians)',
    detail: '(x: number) => number',
    apply: 'sin(/*x*/)',
    boost: 7,
  },
  {
    label: 'cos',
    type: 'function',
    info: 'Return the cosine of x (x is in 0..1 range, not radians)',
    detail: '(x: number) => number',
    apply: 'cos(/*x*/)',
    boost: 7,
  },
  {
    label: 'atan2',
    type: 'function',
    info: 'Return the angle from (0,0) to (dx,dy) in 0..1 range',
    detail: '(dx: number, dy: number) => number',
    apply: 'atan2(/*dx*/, /*dy*/)',
    boost: 5,
  },

  // ==================== RANDOM FUNCTIONS ====================
  {
    label: 'rnd',
    type: 'function',
    info: 'Return a random number from 0 to x (exclusive). Default x=1',
    detail: '(x?: number) => number',
    apply: 'rnd(/*x*/)',
    boost: 9,
  },
  {
    label: 'srand',
    type: 'function',
    info: 'Set the random seed for reproducible randomness',
    detail: '(seed: number) => void',
    apply: 'srand(/*seed*/)',
    boost: 4,
  },

  // ==================== STRING FUNCTIONS ====================
  {
    label: 'sub',
    type: 'function',
    info: 'Return a substring starting at pos (0-indexed) with optional length',
    detail: '(str: string, pos: number, len?: number) => string',
    apply: 'sub(/*str*/, /*pos*/)',
    boost: 5,
  },
  {
    label: 'chr',
    type: 'function',
    info: 'Return a character from its ASCII value',
    detail: '(val: number) => string',
    apply: 'chr(/*val*/)',
    boost: 4,
  },
  {
    label: 'ord',
    type: 'function',
    info: 'Return the ASCII value of a character at position pos (default 0)',
    detail: '(str: string, pos?: number) => number',
    apply: 'ord(/*str*/)',
    boost: 4,
  },

  // ==================== ARRAY FUNCTIONS ====================
  {
    label: 'add',
    type: 'function',
    info: 'Add a value to an array. Optional index to insert at specific position',
    detail: '<T>(arr: T[], val: T, index?: number) => T',
    apply: 'add(/*arr*/, /*val*/)',
    boost: 8,
  },
  {
    label: 'del',
    type: 'function',
    info: 'Remove the first occurrence of a value from an array',
    detail: '<T>(arr: T[], val: T) => T | undefined',
    apply: 'del(/*arr*/, /*val*/)',
    boost: 7,
  },
  {
    label: 'count',
    type: 'function',
    info: 'Return the number of elements in an array or object',
    detail: '(arr: any[] | object) => number',
    apply: 'count(/*arr*/)',
    boost: 6,
  },
  {
    label: 'all',
    type: 'function',
    info: 'Return an iterator for use in for...of loops',
    detail: '<T>(arr: T[]) => T[]',
    apply: 'all(/*arr*/)',
    boost: 5,
  },
  {
    label: 'foreach',
    type: 'function',
    info: 'Call a function for each element in an array',
    detail: '<T>(arr: T[], fn: (item: T) => void) => void',
    apply: 'foreach(/*arr*/, (item) => {\n  /*cursor*/\n})',
    boost: 7,
  },

  // ==================== DEBUG FUNCTIONS ====================
  {
    label: 'printh',
    type: 'function',
    info: 'Print a message to the console log (debug output)',
    detail: '(str: string) => void',
    apply: 'printh(/*str*/)',
    boost: 6,
  },

  // ==================== GAME LOOP FUNCTIONS ====================
  {
    label: '_init',
    type: 'function',
    info: 'Called once when the cart starts. Initialize game state here',
    detail: '() => void',
    apply: 'function _init() {\n  /*cursor*/\n}',
    boost: 10,
  },
  {
    label: '_update',
    type: 'function',
    info: 'Called every frame (60fps). Update game logic here',
    detail: '() => void',
    apply: 'function _update() {\n  /*cursor*/\n}',
    boost: 10,
  },
  {
    label: '_draw',
    type: 'function',
    info: 'Called every frame after _update. Draw the game here',
    detail: '() => void',
    apply: 'function _draw() {\n  /*cursor*/\n}',
    boost: 10,
  },
]

// Common JavaScript keywords and snippets
const JS_SNIPPETS: APIFunction[] = [
  {
    label: 'function',
    type: 'keyword',
    info: 'Declare a function',
    apply: 'function /*name*/() {\n  /*cursor*/\n}',
    boost: 3,
  },
  {
    label: 'let',
    type: 'keyword',
    info: 'Declare a block-scoped variable',
    apply: 'let /*name*/ = ',
    boost: 3,
  },
  {
    label: 'const',
    type: 'keyword',
    info: 'Declare a constant',
    apply: 'const /*name*/ = ',
    boost: 3,
  },
  {
    label: 'if',
    type: 'keyword',
    info: 'Conditional statement',
    apply: 'if (/*condition*/) {\n  /*cursor*/\n}',
    boost: 3,
  },
  {
    label: 'for',
    type: 'keyword',
    info: 'For loop',
    apply:
      'for (let /*i*/ = 0; /*i*/ < /*length*/; /*i*/++) {\n  /*cursor*/\n}',
    boost: 3,
  },
  {
    label: 'while',
    type: 'keyword',
    info: 'While loop',
    apply: 'while (/*condition*/) {\n  /*cursor*/\n}',
    boost: 2,
  },
  {
    label: 'true',
    type: 'keyword',
    info: 'Boolean true value',
    boost: 1,
  },
  {
    label: 'false',
    type: 'keyword',
    info: 'Boolean false value',
    boost: 1,
  },
  {
    label: 'null',
    type: 'keyword',
    info: 'Null value',
    boost: 1,
  },
  {
    label: 'return',
    type: 'keyword',
    info: 'Return from function',
    apply: 'return ',
    boost: 2,
  },
]

// All completions combined
const ALL_COMPLETIONS = [...API_FUNCTIONS, ...JS_SNIPPETS]

/**
 * Custom autocomplete source for Fantasy Console API
 */
export function fantasyConsoleCompletions(
  context: CompletionContext,
): CompletionResult | null {
  // Get the word at the cursor position
  const word = context.matchBefore(/\w*/)

  // Don't show completions if we're not typing a word or at the start
  if (!word || (word.from === word.to && !context.explicit)) {
    return null
  }

  const completions: Completion[] = ALL_COMPLETIONS.map((fn) => ({
    label: fn.label,
    type: fn.type,
    info: fn.info,
    detail: fn.detail,
    apply: fn.apply,
    boost: fn.boost ?? 0,
  }))

  return {
    from: word.from,
    options: completions,
    validFor: /^\w*$/,
  }
}
